import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Divider,
  Stack,
  Link,
  IconButton,
  Tooltip,
  Backdrop,
  CircularProgress,
  ThemeProvider,
  createTheme
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Launch as LaunchIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  Assignment as AssignmentIcon,
  Psychology as PsychologyIcon,
  TrendingUp as TrendingUpIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import AbsenceForm from '../absences/AbsenceForm';
import { useAbsences } from '../../contexts/AbsencesContext';
import './ConflictCheckDialog.css';

const ConflictCheckDialog = ({ 
  open, 
  onClose, 
  results, 
  isLoading, 
  selectedYear, 
  technicians, 
  skills 
}) => {
  const navigate = useNavigate();
  const { absences, loadAbsences } = useAbsences();
  const [expandedSections, setExpandedSections] = useState({
    conflicts: true,
    warnings: false,
    suggestions: false,
    aiAnalysis: false
  });
  
  // États pour la modal d'absence
  const [absenceModal, setAbsenceModal] = useState({
    open: false,
    absence: null,
    technicianId: null
  });

  const handleSectionToggle = (section) => {
    setExpandedSections(prev => {
      const newState = {
        ...prev,
        [section]: !prev[section]
      };
      
      // Si on ouvre la section, faire défiler vers elle après un court délai
      if (!prev[section]) {
        setTimeout(() => {
          const element = document.getElementById(`section-${section}`);
          if (element) {
            element.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start',
              inline: 'nearest'
            });
          }
        }, 100);
      }
      
      return newState;
    });
  };

  const navigateToSection = (section) => {
    // Toujours ouvrir la section
    setExpandedSections(prev => ({
      ...prev,
      [section]: true
    }));
    
    // Faire défiler vers la section après un court délai
    setTimeout(() => {
      const element = document.getElementById(`section-${section}`);
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  // Ouvrir la modal d'absence par-dessus le dialog de conformité
  const handleNavigateToAbsence = (absenceId, technicianId) => {
    const absenceToEdit = absences.find(abs => abs.id === absenceId);
    if (absenceToEdit) {
      setAbsenceModal({
        open: true,
        absence: absenceToEdit,
        technicianId: technicianId
      });
    }
  };

  // Gestion de la modal d'absence
  const handleCloseAbsenceModal = () => {
    setAbsenceModal({
      open: false,
      absence: null,
      technicianId: null
    });
  };

  const handleAbsenceSuccess = () => {
    handleCloseAbsenceModal();
    // Recharger les absences pour mettre à jour les données
    loadAbsences(true);
    // Optionnel : déclencher une nouvelle vérification de conformité
    // Mais laisser l'utilisateur le faire manuellement pour éviter les rechargements automatiques
  };

  // Thème spécialisé pour forcer le z-index des menus au-dessus de la modal
  const menuTheme = createTheme({
    components: {
      MuiPopover: {
        styleOverrides: {
          root: {
            zIndex: '1500 !important'
          }
        }
      },
      MuiMenu: {
        styleOverrides: {
          root: {
            zIndex: '1500 !important'
          }
        }
      },
      MuiModal: {
        styleOverrides: {
          root: {
            zIndex: '1500 !important'
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            '& .MuiPopover-root': {
              zIndex: '1500 !important'
            }
          }
        }
      }
    }
  });

  // Navigation vers l'astreinte ou la semaine problématique dans le planning
  const handleNavigateToOnCall = (onCallId, weekStart = null) => {
    // Nettoyer d'abord les anciens paramètres de navigation
    window.history.replaceState({}, '', window.location.pathname);
    
    // Fermer le dialog
    onClose();
    
    // Naviguer vers le planning avec focus sur l'astreinte ou la semaine
    const navigationState = { 
      selectedYear: selectedYear,
      timestamp: Date.now() // Ajouter un timestamp pour forcer la mise à jour
    };
    
    // Ajouter l'ID d'astreinte à surligner si fourni
    if (onCallId) {
      navigationState.highlightOnCallId = onCallId;
    }
    
    // Ajouter la semaine à centrer si fournie
    if (weekStart) {
      navigationState.focusWeek = weekStart;
    }
    
    navigate('/planning', { state: navigationState });
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'success':
        return <CheckCircleIcon color="success" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'warning':
        return 'warning';
      case 'success':
        return 'success';
      default:
        return 'info';
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      'SKILL_CONFLICT': 'Conflit de compétences',
      'ABSENCE_CONFLICT': 'Conflit d\'absence',
      'COVERAGE_GAP': 'Manque de couverture',
      'REST_PERIOD_VIOLATION': 'Période de repos insuffisante',
      'LOW_SKILL_COVERAGE': 'Couverture faible',
      'WORKLOAD_IMBALANCE': 'Déséquilibre de charge',
      'HOLIDAY_RECOMMENDATION': 'Recommandation jour férié',
      'COMPLIANCE_OK': 'Conformité OK',
      'CRITICAL_ACTIONS_NEEDED': 'Actions critiques requises'
    };
    return labels[type] || type;
  };

  const renderResultItem = (item, index) => {
    const technician = item.technicianId ? technicians[item.technicianId] : null;
    const skill = item.skillId ? skills.find(s => s.id === item.skillId) : null;

    return (
      <Card key={index} sx={{ mb: 2, border: `1px solid ${getSeverityColor(item.severity)}.main` }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {getSeverityIcon(item.severity)}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip 
                  label={getTypeLabel(item.type)} 
                  size="small" 
                  color={getSeverityColor(item.severity)}
                  variant="outlined"
                />
                {item.weekNumber && (
                  <Chip 
                    label={`Semaine ${item.weekNumber}`} 
                    size="small" 
                    variant="outlined"
                  />
                )}
              </Box>
              
              <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
                {item.message}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {item.details}
              </Typography>

              {item.weekPeriod && (
                <Typography variant="caption" color="text.secondary">
                  📅 {item.weekPeriod}
                </Typography>
              )}

              {/* Affichage spécial pour les violations de période de repos */}
              {item.type === 'REST_PERIOD_VIOLATION' && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    📅 Fin astreinte: {item.currentEndDate} → Début suivante: {item.nextStartDate}
                  </Typography>
                  {technician && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      👤 {technician.firstName} {technician.lastName}
                    </Typography>
                  )}
                </>
              )}

              {/* Affichage normal pour les autres types */}
              {item.type !== 'REST_PERIOD_VIOLATION' && technician && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  👤 {technician.firstName} {technician.lastName}
                </Typography>
              )}

              {skill && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  🎯 {skill.name}
                </Typography>
              )}

              {/* Affichage pour conflits d'absence groupés */}
              {item.type === 'ABSENCE_CONFLICT_GROUP' && item.absences && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  📋 Types d'absences: {item.absences.map(abs => {
                    // Fonction locale pour obtenir le label d'affichage
                    const ABSENCE_TYPES = {
                      CP: 'CA', JTL: 'JTL', MALADIE: 'Arrêt maladie', FORMATION: 'Formation',
                      CM: 'CM', TPS: 'Temps partiel', RECUP: 'Récupération', 
                      RECUP_ASTREINTE: 'Récup astreinte', MEDICAL: 'Visite médicale', AUTRE: 'Autre'
                    };
                    return ABSENCE_TYPES[abs.type?.toUpperCase()] || abs.type;
                  }).join(', ')}
                  {item.bypassActivated && ' (BYPASS ACTIVÉ)'}
                </Typography>
              )}

              {/* Affichage pour conflits d'absence individuels (rétrocompatibilité) */}
              {item.type === 'ABSENCE_CONFLICT' && item.absenceType && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  📋 Type d'absence: {item.absenceDisplayLabel || item.absenceType}
                  {item.bypassActivated && ' (BYPASS ACTIVÉ)'}
                  {!item.bypassActivated && ['JTL', 'RECUP_ASTREINTE'].includes(item.absenceType?.toUpperCase()) && ' (Compatible)'}
                  {!item.bypassActivated && ['CA', 'CP', 'ARRET MALADIE', 'MALADIE', 'FORMATION', 'TPS', 'CM', 'RECUP', 'MEDICAL'].includes(item.absenceType?.toUpperCase()) && ' (BLOQUANT)'}
                  {!item.bypassActivated && item.absenceType?.toUpperCase() === 'AUTRE' && ' (À corriger)'}
                </Typography>
              )}

              {item.daysBetween !== undefined && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  ⏰ {item.daysBetween} jour(s) d'écart
                </Typography>
              )}

              {item.coveragePercent !== undefined && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Couverture: {item.coveragePercent}%
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={item.coveragePercent} 
                    sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
                    color={item.coveragePercent > 80 ? 'success' : item.coveragePercent > 60 ? 'warning' : 'error'}
                  />
                </Box>
              )}

              {item.overloaded && item.overloaded.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    ⬆️ Surchargés: {item.overloaded.map(t => `${t.name} (${t.days}j)`).join(', ')}
                  </Typography>
                </Box>
              )}

              {item.underloaded && item.underloaded.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    ⬇️ Sous-chargés: {item.underloaded.map(t => `${t.name} (${t.days}j)`).join(', ')}
                  </Typography>
                </Box>
              )}

              {item.aiGenerated && (
                <Chip 
                  icon={<PsychologyIcon />}
                  label="Recommandation IA" 
                  size="small" 
                  color="info"
                  variant="outlined"
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
            
            {/* Boutons d'action pour navigation */}
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              {/* Bouton pour éditer l'absence - conflit individuel */}
              {item.type === 'ABSENCE_CONFLICT' && item.absenceId && (
                <Tooltip title="Éditer cette absence">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleNavigateToAbsence(item.absenceId, item.technicianId)}
                    sx={{ border: '1px solid', borderColor: 'primary.main' }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              
              {/* Boutons pour éditer les absences - conflit groupé */}
              {item.type === 'ABSENCE_CONFLICT_GROUP' && item.absenceIds && item.absenceIds.length > 0 && (
                <>
                  {item.absenceIds.slice(0, 2).map((absenceId, idx) => (
                    <Tooltip key={idx} title={`Éditer absence ${idx + 1}`}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleNavigateToAbsence(absenceId, item.technicianId)}
                        sx={{ border: '1px solid', borderColor: 'primary.main' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ))}
                  {item.absenceIds.length > 2 && (
                    <Tooltip title={`+${item.absenceIds.length - 2} autres absences`}>
                      <Typography variant="caption" sx={{ alignSelf: 'center', color: 'text.secondary' }}>
                        +{item.absenceIds.length - 2}
                      </Typography>
                    </Tooltip>
                  )}
                </>
              )}
              
              {/* Boutons pour les violations de période de repos */}
              {item.type === 'REST_PERIOD_VIOLATION' && item.currentOnCallId && (
                <>
                  <Tooltip title="Voir la première astreinte">
                    <IconButton
                      size="small"
                      color="warning"
                      onClick={() => handleNavigateToOnCall(item.currentOnCallId, null)}
                      sx={{ border: '1px solid', borderColor: 'warning.main' }}
                    >
                      <LaunchIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {item.nextOnCallId && (
                    <Tooltip title="Voir la deuxième astreinte">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleNavigateToOnCall(item.nextOnCallId, null)}
                        sx={{ border: '1px solid', borderColor: 'error.main' }}
                      >
                        <LaunchIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}
              
              {/* Bouton pour aller à l'astreinte ou la semaine (autres types) */}
              {item.type !== 'REST_PERIOD_VIOLATION' && (item.onCallId || item.weekStart) && (
                <Tooltip title={item.onCallId ? "Voir cette astreinte dans le planning" : "Voir cette semaine dans le planning"}>
                  <IconButton
                    size="small"
                    color="secondary"
                    onClick={() => handleNavigateToOnCall(item.onCallId, item.weekStart)}
                    sx={{ border: '1px solid', borderColor: 'secondary.main' }}
                  >
                    <LaunchIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderAIAnalysis = () => {
    if (!results?.aiAnalysis?.available) return null;

    const { aiAnalysis } = results;

    return (
      <Accordion 
        expanded={expandedSections.aiAnalysis}
        onChange={() => handleSectionToggle('aiAnalysis')}
        sx={{ mb: 2, bgcolor: 'info.light' }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PsychologyIcon color="info" />
            <Typography variant="h6">
              Analyse IA (Confiance: {Math.round(aiAnalysis.confidence * 100)}%)
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {/* Patterns détectés */}
          {aiAnalysis.patterns && aiAnalysis.patterns.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon color="info" />
                Patterns détectés
              </Typography>
              {aiAnalysis.patterns.map((pattern, index) => (
                <Card key={index} sx={{ mb: 2, border: '1px solid', borderColor: 'info.main' }}>
                  <CardContent sx={{ py: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {pattern.description}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {pattern.suggestion}
                    </Typography>
                    <Chip 
                      label={`Impact: ${pattern.impact}`} 
                      size="small" 
                      color={pattern.impact === 'élevé' ? 'error' : pattern.impact === 'moyen' ? 'warning' : 'info'}
                    />
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {/* Prédictions */}
          {aiAnalysis.predictions && aiAnalysis.predictions.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                🔮 Prédictions
              </Typography>
              {aiAnalysis.predictions.map((prediction, index) => (
                <Alert key={index} severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {prediction.description}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {prediction.prediction}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Confiance: {Math.round(prediction.confidence * 100)}% | {prediction.recommendation}
                  </Typography>
                </Alert>
              ))}
            </Box>
          )}

          {/* Recommandations prioritaires */}
          {aiAnalysis.recommendations && aiAnalysis.recommendations.length > 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                💡 Plan d'action recommandé
              </Typography>
              {aiAnalysis.recommendations
                .filter(rec => rec.priority === 'élevée')
                .slice(0, 3)
                .map((rec, index) => (
                <Card key={index} sx={{ mb: 2, border: '1px solid', borderColor: 'warning.main' }}>
                  <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {rec.description}
                      </Typography>
                      <Chip 
                        label={rec.priority} 
                        size="small" 
                        color={rec.priority === 'élevée' ? 'error' : rec.priority === 'moyenne' ? 'warning' : 'default'}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Effort: {rec.estimatedEffort} | Bénéfice: {rec.expectedBenefit}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderSummaryCard = () => {
    if (!results) return null;

    const { summary } = results;
    const totalIssues = summary.criticalErrors + summary.warnings;
    const hasIssues = totalIssues > 0;

    return (
      <Card sx={{ mb: 3, bgcolor: hasIssues ? 'error.light' : 'success.light' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            {hasIssues ? (
              <ErrorIcon color="error" fontSize="large" />
            ) : (
              <CheckCircleIcon color="success" fontSize="large" />
            )}
            <Typography variant="h6">
              {hasIssues ? 'Problèmes détectés' : 'Planning conforme'}
            </Typography>
          </Box>

          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Chip 
              icon={<ErrorIcon />}
              label={`${summary.criticalErrors} Erreurs critiques`}
              color={summary.criticalErrors > 0 ? 'error' : 'default'}
              variant={summary.criticalErrors > 0 ? 'filled' : 'outlined'}
              onClick={() => summary.criticalErrors > 0 && navigateToSection('conflicts')}
              sx={{ 
                cursor: summary.criticalErrors > 0 ? 'pointer' : 'default',
                '&:hover': summary.criticalErrors > 0 ? { opacity: 0.8 } : {}
              }}
            />
            <Chip 
              icon={<WarningIcon />}
              label={`${summary.warnings} Avertissements`}
              color={summary.warnings > 0 ? 'warning' : 'default'}
              variant={summary.warnings > 0 ? 'filled' : 'outlined'}
              onClick={() => summary.warnings > 0 && navigateToSection('warnings')}
              sx={{ 
                cursor: summary.warnings > 0 ? 'pointer' : 'default',
                '&:hover': summary.warnings > 0 ? { opacity: 0.8 } : {}
              }}
            />
            <Chip 
              icon={<InfoIcon />}
              label={`${summary.suggestions} Suggestions`}
              color="info"
              variant="outlined"
              onClick={() => summary.suggestions > 0 && navigateToSection('suggestions')}
              sx={{ 
                cursor: summary.suggestions > 0 ? 'pointer' : 'default',
                '&:hover': summary.suggestions > 0 ? { opacity: 0.8 } : {}
              }}
            />
          </Stack>

          <Typography variant="body2" color="text.secondary">
            Vérification de {results.totalChecks} semaines pour l'année {selectedYear}
          </Typography>

          {summary.coverageGaps > 0 && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
              • {summary.coverageGaps} semaine(s) avec manque de couverture
            </Typography>
          )}
          
          {summary.skillConflicts > 0 && (
            <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
              • {summary.skillConflicts} conflit(s) de compétences
            </Typography>
          )}
          
          {summary.absenceConflicts > 0 && (
            <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
              • {summary.absenceConflicts} conflit(s) avec les absences
            </Typography>
          )}
          
          {summary.restPeriodViolations > 0 && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
              • {summary.restPeriodViolations} violation(s) de période de repos
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <>
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AssignmentIcon />
          Vérification de conformité - {selectedYear}
        </Box>
      </DialogTitle>
      
      <DialogContent dividers sx={{ height: '100%', overflow: 'auto' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
            <LinearProgress sx={{ width: '100%' }} />
            <Typography>Vérification en cours...</Typography>
            <Typography variant="body2" color="text.secondary">
              Analyse des astreintes, absences et périodes de repos
            </Typography>
          </Box>
        ) : results ? (
          <>
            {renderSummaryCard()}

            {/* Analyse IA */}
            {renderAIAnalysis()}

            {/* Erreurs critiques */}
            {results.conflicts && results.conflicts.length > 0 && (
              <Accordion 
                id="section-conflicts"
                expanded={expandedSections.conflicts}
                onChange={() => handleSectionToggle('conflicts')}
                sx={{ mb: 2 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ErrorIcon color="error" />
                    <Typography variant="h6">
                      Erreurs critiques ({results.conflicts.length})
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {results.conflicts.map((conflict, index) => renderResultItem(conflict, index))}
                </AccordionDetails>
              </Accordion>
            )}

            {/* Avertissements */}
            {results.warnings && results.warnings.length > 0 && (
              <Accordion 
                id="section-warnings"
                expanded={expandedSections.warnings}
                onChange={() => handleSectionToggle('warnings')}
                sx={{ mb: 2 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon color="warning" />
                    <Typography variant="h6">
                      Avertissements ({results.warnings.length})
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {results.warnings.map((warning, index) => renderResultItem(warning, index))}
                </AccordionDetails>
              </Accordion>
            )}

            {/* Suggestions */}
            {results.suggestions && results.suggestions.length > 0 && (
              <Accordion 
                id="section-suggestions"
                expanded={expandedSections.suggestions}
                onChange={() => handleSectionToggle('suggestions')}
                sx={{ mb: 2 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon color="info" />
                    <Typography variant="h6">
                      Suggestions d'amélioration ({results.suggestions.length})
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {results.suggestions.map((suggestion, index) => renderResultItem(suggestion, index))}
                </AccordionDetails>
              </Accordion>
            )}
          </>
        ) : (
          <Alert severity="info">
            Cliquez sur "Démarrer la vérification" pour analyser la conformité du planning.
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>
          Fermer
        </Button>
        {!isLoading && results && (
          <Button 
            variant="outlined" 
            onClick={() => {
              // Export des résultats (optionnel)
              console.log('Export des résultats:', results);
            }}
          >
            Exporter le rapport
          </Button>
        )}
      </DialogActions>
    </Dialog>
    
    {/* Modal d'édition d'absence par-dessus le dialog de conformité */}
    <Dialog
      open={absenceModal.open}
      onClose={handleCloseAbsenceModal}
      maxWidth="md"
      fullWidth
      sx={{ 
        zIndex: 1300 // Z-index standard pour la modal d'absence
      }}
    >
      <DialogTitle>
        Modifier l'absence
        <IconButton
          onClick={handleCloseAbsenceModal}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {absenceModal.absence && (
          <ThemeProvider theme={menuTheme}>
            <AbsenceForm
              absence={absenceModal.absence}
              onClose={handleCloseAbsenceModal}
              onSuccess={handleAbsenceSuccess}
              technicianId={absenceModal.technicianId}
            />
          </ThemeProvider>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};

export default ConflictCheckDialog;
